// src/utils/caip-2.ts
var BlockchainIds = {
  SOLANA_MAINNET: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  SOLANA_DEVNET: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  SOLANA_TESTNET: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3",
  ETHEREUM_MAINNET: "eip155:1",
  ETHEREUM_SEPOLIA: "eip155:11155111",
  MONAD_DEVNET: "eip155:20143"
};
var BlockchainNames = {
  [BlockchainIds.SOLANA_MAINNET]: "Solana Mainnet",
  [BlockchainIds.SOLANA_DEVNET]: "Solana Devnet",
  [BlockchainIds.SOLANA_TESTNET]: "Solana Testnet",
  [BlockchainIds.ETHEREUM_MAINNET]: "Ethereum Mainnet",
  [BlockchainIds.ETHEREUM_SEPOLIA]: "Ethereum Sepolia",
  [BlockchainIds.MONAD_DEVNET]: "Monad Devnet"
};
function getShortBlockchainName(id) {
  const blockchainName = BlockchainNames[id];
  if (!blockchainName) {
    const [chainId, reference] = id.split(":");
    if (chainId && reference) {
      const truncatedReference = reference.length > 3 ? reference.slice(0, 3) + "..." : reference;
      return `${chainId}:${truncatedReference}`;
    } else {
      return id.length > 8 ? id.slice(0, 8) + "..." : id;
    }
  }
  return blockchainName;
}

// src/utils/client-key.ts
var BLINK_CLIENT_KEY_HEADER = "x-blink-client-key";
var clientKey = null;
function setClientKey(key) {
  if (!key) {
    console.warn("[@dialectlabs/blinks] client key is not set");
    clientKey = null;
    return;
  }
  clientKey = key;
}

// src/utils/constants.ts
var SOLANA_ACTION_PREFIX = /^(solana-action:|solana:|blink:|evm:|evm-action:)/;
var EMPTY_OBJECT = Object.freeze({});

// src/utils/interstitial-url.ts
function isInterstitial(url) {
  try {
    const urlObj = new URL(url);
    const actionUrl = urlObj.searchParams.get("action");
    if (!actionUrl) {
      return { isInterstitial: false };
    }
    const urlDecodedActionUrl = decodeURIComponent(actionUrl);
    if (!SOLANA_ACTION_PREFIX.test(urlDecodedActionUrl)) {
      return { isInterstitial: false };
    }
    const decodedActionUrl = urlDecodedActionUrl.replace(
      SOLANA_ACTION_PREFIX,
      ""
    );
    const decodedActionUrlObj = new URL(decodedActionUrl);
    return {
      isInterstitial: true,
      decodedActionUrl: decodedActionUrlObj.toString()
    };
  } catch (e) {
    console.error(
      `[@dialectlabs/blinks] Failed to check if URL is interstitial: ${url}`,
      e
    );
    return { isInterstitial: false };
  }
}

// src/utils/proxify.ts
var proxyUrl = "https://proxy.dial.to";
function setProxyUrl(url) {
  if (!url) {
    console.warn(
      "[@dialectlabs/blinks-core] Proxy URL is not set, proxy will be disabled"
    );
    proxyUrl = null;
    return;
  }
  try {
    new URL(url);
  } catch (e) {
    console.warn("[@dialectlabs/blinks-core] Invalid proxy URL", e);
    return;
  }
  proxyUrl = url;
}
function proxify(url) {
  return createProxifiedUrl(url);
}
function proxifyImage(url) {
  return createProxifiedUrl(url, "image");
}
function proxifyMetadata(url) {
  return createProxifiedUrl(url, "metadata");
}
function isProxified(url) {
  return proxyUrl ? url.startsWith(proxyUrl) : false;
}
function createProxifiedUrl(url, endpoint) {
  const incomingUrl = new URL(url);
  if (!proxyUrl || shouldIgnoreProxy(incomingUrl)) {
    return {
      url: incomingUrl,
      headers: {}
    };
  }
  if (shouldPreserveOriginal(incomingUrl)) {
    return {
      url: incomingUrl,
      headers: getProxifiedHeaders()
    };
  }
  const proxifiedUrl = endpoint ? new URL(endpoint, proxyUrl) : new URL(proxyUrl);
  proxifiedUrl.searchParams.set("url", url);
  return {
    url: proxifiedUrl,
    headers: getProxifiedHeaders()
  };
}
function getProxifiedHeaders() {
  return {
    ...clientKey && { [BLINK_CLIENT_KEY_HEADER]: clientKey }
  };
}
function shouldIgnoreProxy(url) {
  return url.hostname === "localhost" || url.hostname === "127.0.0.1";
}
function shouldPreserveOriginal(url) {
  return url.hostname === "api.dial.to";
}

// src/utils/security.ts
var checkSecurity = (state, securityLevel) => {
  switch (securityLevel) {
    case "only-trusted":
      return state === "trusted";
    case "non-malicious":
      return state !== "malicious";
    case "all":
      return true;
  }
};
var isUrlSameOrigin = (origin, url) => {
  if (!url.startsWith("http")) {
    return true;
  }
  const urlObj = new URL(url);
  return urlObj.origin === origin;
};

// src/utils/supportability.ts
var getBlinkSupportabilityMetadata = (response) => {
  const blockchainIds = response.headers.get("x-blockchain-ids")?.split(",").map((id) => id.trim());
  const version = response.headers.get("x-action-version")?.trim();
  return {
    blockchainIds,
    version
  };
};

// src/utils/url-mapper.ts
var BlinksURLMapper = class {
  config;
  constructor(config) {
    this.config = config;
  }
  mapUrl(url) {
    const urlObj = typeof url === "string" ? new URL(url) : url;
    const queryParams = urlObj.search;
    for (const action of this.config.rules) {
      if (this.isExactMatch(action.pathPattern, urlObj)) {
        return `${action.apiPath}${queryParams}`;
      }
      const match = this.matchPattern(action.pathPattern, urlObj);
      if (match) {
        return this.constructMappedUrl(
          action.apiPath,
          match,
          queryParams,
          urlObj.origin
        );
      }
    }
    return null;
  }
  // Helper method to check for exact match
  isExactMatch(pattern, urlObj) {
    return pattern === `${urlObj.origin}${urlObj.pathname}`;
  }
  // Helper method to match the URL with the pattern
  matchPattern(pattern, urlObj) {
    const fullPattern = new RegExp(
      `^${pattern.replace(/\*\*/g, "(.*)").replace(/\/(\*)/g, "/([^/]+)")}$`
    );
    const urlToMatch = pattern.startsWith("http") ? urlObj.toString() : urlObj.pathname;
    return urlToMatch.match(fullPattern);
  }
  // Helper method to construct the mapped URL
  constructMappedUrl(apiPath, match, queryParams, origin) {
    let mappedPath = apiPath;
    match.slice(1).forEach((group) => {
      mappedPath = mappedPath.replace(/\*+/, group);
    });
    if (apiPath.startsWith("http")) {
      const mappedUrl = new URL(mappedPath);
      return `${mappedUrl.origin}${mappedUrl.pathname}${queryParams}`;
    }
    return `${origin}${mappedPath}${queryParams}`;
  }
};
async function unfurlUrlToBlinkApiUrl(actionUrl) {
  const url = new URL(actionUrl);
  const strUrl = actionUrl.toString();
  if (SOLANA_ACTION_PREFIX.test(strUrl)) {
    return strUrl.replace(SOLANA_ACTION_PREFIX, "");
  }
  const interstitialData = isInterstitial(url);
  if (interstitialData.isInterstitial) {
    return interstitialData.decodedActionUrl;
  }
  const actionsJsonUrl = url.origin + "/actions.json";
  const { url: proxyUrl2, headers: proxyHeaders } = proxify(actionsJsonUrl);
  const actionsJson = await fetch(proxyUrl2, {
    headers: {
      ...proxyHeaders
    }
  }).then((res) => res.json());
  const actionsUrlMapper = new BlinksURLMapper(actionsJson);
  return actionsUrlMapper.mapUrl(url);
}

// package.json
var package_default = {
  name: "@dialectlabs/blinks-core",
  version: "0.20.6",
  license: "Apache-2.0",
  private: false,
  sideEffects: true,
  type: "module",
  repository: {
    type: "git",
    url: "https://github.com/dialectlabs/blinks"
  },
  scripts: {
    build: "tsup-node",
    dev: "tsup-node --watch"
  },
  main: "dist/index.cjs",
  module: "dist/index.js",
  types: "dist/index.d.ts",
  exports: {
    "./api": {
      import: "./dist/service-api/index.js",
      require: "./dist/service-api/index.cjs",
      types: "./dist/service-api/index.d.ts"
    },
    "./solana": {
      import: "./dist/solana/index.js",
      require: "./dist/solana/index.cjs",
      types: "./dist/solana/index.d.ts"
    },
    ".": {
      import: "./dist/index.js",
      require: "./dist/index.cjs",
      types: "./dist/index.d.ts"
    }
  },
  files: [
    "dist"
  ],
  devDependencies: {
    "@solana/actions-spec": "~2.4.2",
    "@types/react": "^18.3.3",
    "@types/react-dom": "^18.3.0",
    "@typescript-eslint/eslint-plugin": "^7.16.1",
    "@typescript-eslint/parser": "^7.16.1",
    autoprefixer: "^10.4.19",
    eslint: "^8.57.0",
    "eslint-config-prettier": "^9.1.0",
    "eslint-plugin-react": "^7.34.4",
    "eslint-plugin-react-hooks": "^4.6.2",
    postcss: "^8.4.39",
    "postcss-prefix-selector": "^1.16.1",
    prettier: "^3.3.3",
    "prettier-plugin-organize-imports": "^4.0.0",
    "prettier-plugin-tailwindcss": "^0.6.5",
    tailwindcss: "^3.4.3",
    tsup: "^8.2.0",
    typescript: "^5.5.3"
  },
  peerDependencies: {
    react: ">=18",
    "@solana/web3.js": "^1.95.3"
  },
  dependencies: {
    nanoid: "^5.0.9"
  },
  peerDependenciesMeta: {
    "@solana/web3.js": {
      optional: true
    }
  }
};

// src/utils/dependency-versions.ts
var pkg = package_default;
var ACTIONS_SPEC_VERSION = pkg.devDependencies["@solana/actions-spec"].replace(/[^\d.]/g, "");

// src/api/BlinkInstance/blink-supportability.ts
var MAX_SUPPORTED_ACTION_VERSION = ACTIONS_SPEC_VERSION;
var DEFAULT_SUPPORTED_BLOCKCHAIN_IDS = [
  BlockchainIds.SOLANA_MAINNET,
  BlockchainIds.SOLANA_DEVNET
];
var BASELINE_ACTION_VERSION = "2.2";
var BASELINE_BLINK_BLOCKCHAIN_IDS = [BlockchainIds.SOLANA_MAINNET];
var defaultBlinkSupportStrategy = async (blink, adapter) => {
  const { version: actionSpecVersion, blockchainIds: blinkBlockchainIds } = blink.metadata;
  if (!actionSpecVersion || !blinkBlockchainIds || blinkBlockchainIds.length === 0) {
    return {
      isSupported: false,
      message: "Blink compatibility metadata is not set. Please contact the blink provider."
    };
  }
  const supportedActionVersion = MAX_SUPPORTED_ACTION_VERSION;
  const supportedBlockchainIds = adapter.metadata.supportedBlockchainIds;
  const versionSupported = isVersionSupported({
    actionVersion: actionSpecVersion,
    supportedActionVersion
  });
  const blockchainSupported = isBlockchainSupported({
    actionBlockchainIds: blinkBlockchainIds,
    supportedBlockchainIds
  });
  const notSupportedBlockchainIds = blinkBlockchainIds.filter(
    (id) => !supportedBlockchainIds.includes(id)
  );
  const notSupportedActionBlockchainNames = notSupportedBlockchainIds.map(
    getShortBlockchainName
  );
  if (!versionSupported && !blockchainSupported) {
    const blockchainMessage = notSupportedActionBlockchainNames.length === 1 ? `blockchain ${notSupportedActionBlockchainNames[0]}` : `blockchains ${notSupportedActionBlockchainNames.join(", ")}`;
    return {
      isSupported: false,
      message: `Action version ${actionSpecVersion} and ${blockchainMessage} are not supported by your Blink client.`
    };
  }
  if (!versionSupported) {
    return {
      isSupported: false,
      message: `Action version ${actionSpecVersion} is not supported by your Blink client.`
    };
  }
  if (!blockchainSupported) {
    const blockchainMessage = notSupportedActionBlockchainNames.length === 1 ? `Action blockchain ${notSupportedActionBlockchainNames[0]} is not supported by your Blink client.` : `Action blockchains ${notSupportedActionBlockchainNames.join(", ")} are not supported by your Blink client.`;
    return {
      isSupported: false,
      message: blockchainMessage
    };
  }
  return {
    isSupported: true
  };
};
function isVersionSupported({
  supportedActionVersion,
  actionVersion
}) {
  return compareSemverIgnoringPatch(actionVersion, supportedActionVersion) <= 0;
}
function compareSemverIgnoringPatch(v1, v2) {
  const [major1, minor1] = v1.split(".").map(Number);
  const [major2, minor2] = v2.split(".").map(Number);
  if (major1 !== major2) {
    return major1 - major2;
  } else if (minor1 !== minor2) {
    return minor1 - minor2;
  }
  return 0;
}
function isBlockchainSupported({
  supportedBlockchainIds,
  actionBlockchainIds
}) {
  if (actionBlockchainIds.length === 0 || supportedBlockchainIds.length === 0) {
    return false;
  }
  const sanitizedSupportedBlockchainIds = supportedBlockchainIds.map(
    (it) => it.trim()
  );
  const sanitizedActionBlockchainIds = actionBlockchainIds.map(
    (it) => it.trim()
  );
  return sanitizedActionBlockchainIds.every(
    (chain) => sanitizedSupportedBlockchainIds.includes(chain)
  );
}

// src/api/BlinkInstance/BlinkInstance.ts
import { nanoid } from "nanoid/non-secure";

// src/api/BlinkInstance/action-components/AbstractActionComponent.ts
var AbstractActionComponent = class {
  constructor(_parent, _label, _href, _type, _parameters) {
    this._parent = _parent;
    this._label = _label;
    this._href = _href;
    this._type = _type;
    this._parameters = _parameters;
  }
  get parent() {
    return this._parent;
  }
  get label() {
    return this._label;
  }
  get parameters() {
    return this._parameters ?? [];
  }
  get type() {
    return this._type;
  }
  async post(account) {
    const { url: proxyUrl2, headers: proxyHeaders } = proxify(this.href);
    const response = await fetch(proxyUrl2, {
      method: "POST",
      body: JSON.stringify(this.buildBody(account)),
      headers: {
        "Content-Type": "application/json",
        ...proxyHeaders
      }
    });
    if (!response.ok) {
      const error = await response.json();
      console.error(
        `[@dialectlabs/blinks] Failed to execute action ${proxyUrl2}, href ${this.href}, reason: ${error.message}`
      );
      throw {
        message: error.message
      };
    }
    return await response.json();
  }
};

// src/api/BlinkInstance/action-components/ButtonActionComponent.ts
var ButtonActionComponent = class extends AbstractActionComponent {
  constructor(_parent, _label, _href, _type, _parameters, _parentComponent) {
    super(_parent, _label, _href, _type, _parameters);
    this._parent = _parent;
    this._label = _label;
    this._href = _href;
    this._type = _type;
    this._parameters = _parameters;
    this._parentComponent = _parentComponent;
  }
  get parentComponent() {
    return this._parentComponent ?? null;
  }
  buildBody(account) {
    return { account, type: this.type };
  }
  get href() {
    return this._href;
  }
};

// src/api/BlinkInstance/action-components/SingleValueActionComponent.ts
var SingleValueActionComponent = class extends AbstractActionComponent {
  constructor(_parent, _label, _href, _type, _parameters, _parentComponent) {
    super(_parent, _label, _href, _type, _parameters);
    this._parent = _parent;
    this._label = _label;
    this._href = _href;
    this._type = _type;
    this._parameters = _parameters;
    this._parentComponent = _parentComponent;
  }
  parameterValue = null;
  get parentComponent() {
    return this._parentComponent ?? null;
  }
  buildBody(account) {
    if (this._href.indexOf(`{${this.parameter.name}}`) > -1 || this.parameterValue === null) {
      return { account, type: this.type };
    }
    return {
      account,
      type: this.type,
      data: {
        [this.parameter.name]: this.parameterValue
      }
    };
  }
  get parameter() {
    const [param] = this.parameters;
    return param;
  }
  setValue(value) {
    this.parameterValue = value;
  }
  get href() {
    return this._href.replace(
      `{${this.parameter.name}}`,
      encodeURIComponent(this.parameterValue?.toString().trim() ?? "")
    );
  }
  toButtonActionComponent() {
    return new ButtonActionComponent(
      this._parent,
      this._label,
      this._href,
      this._type,
      void 0,
      this
    );
  }
};

// src/api/BlinkInstance/action-components/FormActionComponent.ts
var FormActionComponent = class extends AbstractActionComponent {
  constructor(_parent, _label, _href, _type, _parameters, _parentComponent) {
    super(_parent, _label, _href, _type, _parameters);
    this._parent = _parent;
    this._label = _label;
    this._href = _href;
    this._type = _type;
    this._parameters = _parameters;
    this._parentComponent = _parentComponent;
  }
  parameterValues = {};
  get parentComponent() {
    return this._parentComponent ?? null;
  }
  // any, since we don't know the parameter names on the client level
  buildBody(account) {
    const paramNames = Object.keys(this.parameterValues);
    const bodyParams = [];
    paramNames.forEach((paramName) => {
      if (this._href.indexOf(`{${paramName}}`) === -1) {
        bodyParams.push(paramName);
      }
    });
    if (bodyParams.length > 0) {
      return {
        account,
        type: this.type,
        data: Object.fromEntries(
          paramNames.filter((name) => bodyParams.includes(name)).map((paramName) => [paramName, this.parameterValues[paramName]])
        )
      };
    }
    return { account, type: this.type };
  }
  get href() {
    const replacedHref = this.parameters.reduce((href, param) => {
      const value = this.parameterValues[param.name];
      if (!value) {
        return href;
      }
      return href.replace(
        `{${param.name}}`,
        encodeURIComponent(
          typeof value === "string" ? value : value?.join(",")
        )
      );
    }, this._href);
    return replacedHref.replaceAll(/={[^}]+}&/g, "=&").replaceAll(/={[^}]+}/g, "=");
  }
  setValue(value, name) {
    this.parameterValues[name] = value;
  }
  toButtonActionComponent() {
    return new ButtonActionComponent(
      this._parent,
      this._label,
      this.href,
      this._type,
      void 0,
      this
    );
  }
  toInputActionComponent(paramName) {
    const parameter = this.parameters.find((param) => param.name === paramName);
    if (!parameter) {
      throw new Error(`Input Parameter ${paramName} not found`);
    }
    return new SingleValueActionComponent(
      this._parent,
      this._label,
      this._href,
      this._type,
      [parameter],
      this
    );
  }
};

// src/api/BlinkInstance/action-components/MultiValueActionComponent.ts
var MultiValueActionComponent = class extends AbstractActionComponent {
  constructor(_parent, _label, _href, _type, _parameters, _parentComponent) {
    super(_parent, _label, _href, _type, _parameters);
    this._parent = _parent;
    this._label = _label;
    this._href = _href;
    this._type = _type;
    this._parameters = _parameters;
    this._parentComponent = _parentComponent;
  }
  parameterValue = [];
  get parentComponent() {
    return this._parentComponent ?? null;
  }
  // any, since we don't know the parameter names on the client level
  buildBody(account) {
    if (this._href.indexOf(`{${this.parameter.name}}`) > -1) {
      return { account, type: this.type };
    }
    return {
      account,
      type: this.type,
      data: {
        [this.parameter.name]: this.isMultiOptions ? this.parameterValue : this.parameterValue[0]
      }
    };
  }
  get isMultiOptions() {
    return this.parameter.type === "checkbox";
  }
  get parameter() {
    const [param] = this.parameters;
    return param;
  }
  setValue(value) {
    this.parameterValue = typeof value === "string" ? [value] : value;
  }
  get href() {
    return this._href.replace(
      `{${this.parameter.name}}`,
      encodeURIComponent(this.parameterValue.join(","))
    );
  }
  toButtonActionComponent() {
    return new ButtonActionComponent(
      this._parent,
      this._label,
      this._href,
      this._type,
      void 0,
      this
    );
  }
};

// src/api/BlinkInstance/action-components/guards.ts
var isPatternAllowed = (parameter) => {
  return parameter.type !== "select" && parameter.type !== "radio" && parameter.type !== "checkbox";
};
var isParameterSelectable = (parameter) => {
  return parameter.type === "select" || parameter.type === "radio" || parameter.type === "checkbox";
};

// src/api/BlinkInstance/BlinkInstance.ts
var MULTI_VALUE_TYPES = ["checkbox"];
var EXPERIMENTAL_LIVE_DATA_DEFAULT_DELAY_MS = 1e3;
var BlinkInstance6 = class _BlinkInstance {
  constructor(_url, _data, _metadata, _supportStrategy, _chainMetadata = { isChained: false }, _id, _experimental) {
    this._url = _url;
    this._data = _data;
    this._metadata = _metadata;
    this._supportStrategy = _supportStrategy;
    this._chainMetadata = _chainMetadata;
    this._id = _id;
    this._experimental = _experimental;
    if (_data.type === "completed" || !_data.links?.actions) {
      this._actions = [
        new ButtonActionComponent(this, _data.label, _url, "transaction")
      ];
      return;
    }
    const urlObj = new URL(_url);
    this._actions = _data.links.actions.map((action) => {
      const href = action.href.startsWith("http") ? action.href : urlObj.origin + action.href;
      return componentFactory(
        this,
        action.label,
        href,
        action.type ?? "transaction",
        action.parameters
      );
    });
  }
  _actions;
  // this API MAY change in the future
  get liveData_experimental() {
    const liveData = this._experimental?.liveData;
    if (!liveData) {
      return null;
    }
    return {
      enabled: liveData.enabled,
      delayMs: liveData.delayMs ? Math.max(liveData.delayMs, EXPERIMENTAL_LIVE_DATA_DEFAULT_DELAY_MS) : EXPERIMENTAL_LIVE_DATA_DEFAULT_DELAY_MS
    };
  }
  get id() {
    return this._id;
  }
  get data() {
    return this._data;
  }
  get supportStrategy() {
    return this._supportStrategy;
  }
  get isChained() {
    return this._chainMetadata.isChained;
  }
  get isInline() {
    return this._chainMetadata.isChained ? this._chainMetadata.isInline : false;
  }
  get type() {
    return this._data.type;
  }
  get url() {
    return this._url;
  }
  get icon() {
    if (this._data.icon.startsWith("data:") || isProxified(this._data.icon) || !this._data.icon) {
      return this._data.icon;
    }
    return proxifyImage(this._data.icon).url.toString();
  }
  get title() {
    return this._data.title;
  }
  get description() {
    return this._data.description;
  }
  get disabled() {
    return this._data.disabled ?? false;
  }
  get actions() {
    return this._actions;
  }
  get error() {
    return this._data.error?.message ?? null;
  }
  get message() {
    return this._data.message ?? null;
  }
  get metadata() {
    return {
      blockchainIds: this._metadata.blockchainIds ?? BASELINE_BLINK_BLOCKCHAIN_IDS,
      version: this._metadata.version ?? BASELINE_ACTION_VERSION
    };
  }
  async isSupported(adapter) {
    try {
      return await this._supportStrategy(this, adapter);
    } catch (e) {
      console.error(
        `[@dialectlabs/blinks] Failed to check supportability for action ${this.url}`,
        e
      );
      return {
        isSupported: false,
        message: "Failed to check supportability, please contact your Blink client provider."
      };
    }
  }
  async chain(next, chainData, lifecycleData) {
    const id = nanoid();
    if (next.type === "inline") {
      return new _BlinkInstance(
        this.url,
        lifecycleData ? mergeLifecycleData(next.action, lifecycleData) : next.action,
        this.metadata,
        this._supportStrategy,
        {
          isChained: true,
          isInline: true
        },
        id
      );
    }
    const baseUrlObj = new URL(this.url);
    if (!isUrlSameOrigin(baseUrlObj.origin, next.href)) {
      console.error(
        `Chained action is not the same origin as the current action. Original: ${this.url}, chained: ${next.href}`
      );
      return null;
    }
    const href = next.href.startsWith("http") ? next.href : baseUrlObj.origin + next.href;
    const { url: proxyUrl2, headers: proxyHeaders } = proxify(href);
    const response = await fetch(proxyUrl2, {
      method: "POST",
      body: JSON.stringify(chainData),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...proxyHeaders
      }
    });
    if (!response.ok) {
      console.error(
        `Failed to fetch chained action ${proxyUrl2}, action url: ${next.href}`
      );
      return null;
    }
    const data = await response.json();
    const metadata = getBlinkSupportabilityMetadata(response);
    return new _BlinkInstance(
      href,
      lifecycleData ? mergeLifecycleData(data, lifecycleData) : data,
      metadata,
      this._supportStrategy,
      {
        isChained: true,
        isInline: false
      },
      id
    );
  }
  safeInlineChain(lifecycleData) {
    if (!lifecycleData) {
      return this;
    }
    const id = nanoid();
    return new _BlinkInstance(
      this.url,
      mergeLifecycleData(this._data, lifecycleData),
      this.metadata,
      this._supportStrategy,
      {
        isChained: true,
        isInline: true
      },
      id
    );
  }
  // be sure to use this only if the action is valid
  static hydrate(url, data, metadata, supportStrategy) {
    const id = nanoid();
    return new _BlinkInstance(
      url,
      data,
      metadata,
      supportStrategy,
      { isChained: false },
      id
    );
  }
  static async _fetch(apiUrl, supportStrategy = defaultBlinkSupportStrategy, chainMetadata, id) {
    const { url: proxyUrl2, headers: proxyHeaders } = proxify(apiUrl);
    const response = await fetch(proxyUrl2, {
      headers: {
        Accept: "application/json",
        ...proxyHeaders
      }
    });
    if (!response.ok) {
      throw new Error(
        `Failed to fetch action ${proxyUrl2}, action url: ${apiUrl}`
      );
    }
    const data = await response.json();
    const metadata = getBlinkSupportabilityMetadata(response);
    return new _BlinkInstance(
      apiUrl,
      { ...data, type: "action" },
      metadata,
      supportStrategy,
      chainMetadata,
      id,
      data.dialectExperimental
    );
  }
  static async fetch(apiUrl, supportStrategy = defaultBlinkSupportStrategy) {
    const id = nanoid();
    return _BlinkInstance._fetch(
      apiUrl,
      supportStrategy,
      {
        isChained: false
      },
      id
    );
  }
  refresh() {
    return _BlinkInstance._fetch(
      this.url,
      this._supportStrategy,
      this._chainMetadata,
      this._id
    );
  }
  withUpdate(update) {
    return new _BlinkInstance(
      this._url,
      this._data,
      this._metadata,
      update.supportStrategy ?? this._supportStrategy,
      this._chainMetadata,
      this._id,
      this._experimental
    );
  }
};
var componentFactory = (parent, label, href, type, parameters) => {
  if (!parameters?.length) {
    return new ButtonActionComponent(parent, label, href, type);
  }
  if (parameters.length > 1) {
    return new FormActionComponent(parent, label, href, type, parameters);
  }
  const [parameter] = parameters;
  if (!parameter.type) {
    return new SingleValueActionComponent(
      parent,
      label,
      href,
      type,
      parameters
    );
  }
  if (MULTI_VALUE_TYPES.includes(parameter.type)) {
    return new MultiValueActionComponent(parent, label, href, type, parameters);
  }
  return new SingleValueActionComponent(parent, label, href, type, parameters);
};
var mergeLifecycleData = (action, lifecycleData) => {
  const links = [];
  if (action.type !== "completed" && action.links) {
    links.push(...action.links.actions ?? []);
  }
  if (lifecycleData.links?.actions) {
    links.push(...lifecycleData.links.actions);
  }
  return {
    ...action,
    type: "action",
    // if lifecycle data present, we are not in a completed state
    message: lifecycleData.message ?? action.message,
    links: links.length > 0 ? { actions: links } : void 0
  };
};

// src/api/BlinkInstance/sign-message-data.ts
var SignMessageVerificationErrorType = /* @__PURE__ */ ((SignMessageVerificationErrorType2) => {
  SignMessageVerificationErrorType2["ADDRESS_MISMATCH"] = "ADDRESS_MISMATCH";
  SignMessageVerificationErrorType2["DOMAIN_MISMATCH"] = "DOMAIN_MISMATCH";
  SignMessageVerificationErrorType2["CHAIN_ID_MISMATCH"] = "CHAIN_ID_MISMATCH";
  SignMessageVerificationErrorType2["ISSUED_TOO_FAR_IN_THE_PAST"] = "ISSUED_TOO_FAR_IN_THE_PAST";
  SignMessageVerificationErrorType2["ISSUED_TOO_FAR_IN_THE_FUTURE"] = "ISSUED_TOO_FAR_IN_THE_FUTURE";
  SignMessageVerificationErrorType2["INVALID_DATA"] = "INVALID_DATA";
  return SignMessageVerificationErrorType2;
})(SignMessageVerificationErrorType || {});
var DOMAIN = "(?<domain>[^\\n]+?) wants you to sign a message with your account:\\n";
var ADDRESS = "(?<address>[^\\n]+)(?:\\n|$)";
var STATEMENT = "(?:\\n(?<statement>[\\S\\s]*?)(?:\\n|$))";
var CHAIN_ID = "(?:\\nChain ID: (?<chainId>[^\\n]+))?";
var NONCE = "\\nNonce: (?<nonce>[^\\n]+)";
var ISSUED_AT = "\\nIssued At: (?<issuedAt>[^\\n]+)";
var FIELDS = `${CHAIN_ID}${NONCE}${ISSUED_AT}`;
var MESSAGE = new RegExp(`^${DOMAIN}${ADDRESS}${STATEMENT}${FIELDS}\\n*$`);
function createSignMessageText(input) {
  let message = `${input.domain} wants you to sign a message with your account:
`;
  message += `${input.address}`;
  message += `

${input.statement}`;
  const fields = [];
  if (input.chainId) {
    fields.push(`Chain ID: ${input.chainId}`);
  }
  fields.push(`Nonce: ${input.nonce}`);
  fields.push(`Issued At: ${input.issuedAt}`);
  message += `

${fields.join("\n")}`;
  return message;
}
function parseSignMessageText(text) {
  const match = MESSAGE.exec(text);
  if (!match) return null;
  const groups = match.groups;
  if (!groups) return null;
  return {
    domain: groups.domain,
    address: groups.address,
    statement: groups.statement,
    nonce: groups.nonce,
    chainId: groups.chainId,
    issuedAt: groups.issuedAt
  };
}
function verifySignMessageData(data, opts) {
  if (!data.address || !data.domain || !data.issuedAt || !data.nonce || !data.statement) {
    return ["INVALID_DATA" /* INVALID_DATA */];
  }
  try {
    const {
      expectedAddress,
      expectedChainIds,
      issuedAtThreshold,
      expectedDomains
    } = opts;
    const errors = [];
    const now = Date.now();
    if (expectedAddress && data.address !== expectedAddress) {
      errors.push("ADDRESS_MISMATCH" /* ADDRESS_MISMATCH */);
    }
    if (expectedDomains) {
      const expectedDomainsNormalized = expectedDomains.map(normalizeDomain);
      const normalizedDomain = normalizeDomain(data.domain);
      if (!expectedDomainsNormalized.includes(normalizedDomain)) {
        errors.push("DOMAIN_MISMATCH" /* DOMAIN_MISMATCH */);
      }
    }
    if (expectedChainIds && data.chainId && !expectedChainIds.includes(data.chainId)) {
      errors.push("CHAIN_ID_MISMATCH" /* CHAIN_ID_MISMATCH */);
    }
    if (issuedAtThreshold !== void 0) {
      const iat = Date.parse(data.issuedAt);
      if (Math.abs(iat - now) > issuedAtThreshold) {
        if (iat < now) {
          errors.push(
            "ISSUED_TOO_FAR_IN_THE_PAST" /* ISSUED_TOO_FAR_IN_THE_PAST */
          );
        } else {
          errors.push(
            "ISSUED_TOO_FAR_IN_THE_FUTURE" /* ISSUED_TOO_FAR_IN_THE_FUTURE */
          );
        }
      }
    }
    return errors;
  } catch (e) {
    return ["INVALID_DATA" /* INVALID_DATA */];
  }
}
function normalizeDomain(domain) {
  return domain.replace(/^www\./, "");
}

// src/api/BlinksRegistry.ts
var DEFAULT_REFRESH_INTERVAL = 10 * 60 * 1e3;
var BlinksRegistry = class _BlinksRegistry {
  static instance = null;
  blinksByHost;
  websitesByHost;
  interstitialsByHost;
  intervalId = null;
  constructor(config) {
    this.blinksByHost = config ? Object.fromEntries(
      config.actions.map((action) => [action.host, action])
    ) : {};
    this.websitesByHost = config ? Object.fromEntries(
      config.websites.map((website) => [website.host, website])
    ) : {};
    this.interstitialsByHost = config ? Object.fromEntries(
      config.interstitials.map((interstitial) => [
        interstitial.host,
        interstitial
      ])
    ) : {};
  }
  static getInstance(config) {
    if (this.instance === null || config) {
      this.instance = new _BlinksRegistry(config);
    }
    return this.instance;
  }
  async init() {
    if (this.intervalId !== null) {
      return;
    }
    await this.refresh();
    this.intervalId = setInterval(
      () => this.refresh(),
      DEFAULT_REFRESH_INTERVAL
    );
  }
  stopRefresh() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
  async refresh() {
    const config = await fetchBlinksRegistryConfig();
    this.blinksByHost = Object.fromEntries(
      config.actions.map((action) => [action.host, action])
    );
    this.websitesByHost = Object.fromEntries(
      config.websites.map((website) => [website.host, website])
    );
    this.interstitialsByHost = Object.fromEntries(
      config.interstitials.map((interstitial) => [
        interstitial.host,
        interstitial
      ])
    );
  }
  lookup(url, type = "action") {
    if (type === "action" || type === "blink") {
      return this.lookupBlink(url);
    }
    if (type === "website") {
      return this.lookupWebsite(url);
    }
    if (type === "interstitial") {
      return this.lookupInterstitial(url);
    }
    return null;
  }
  lookupBlink(url) {
    try {
      const urlObj = new URL(url);
      const host = urlObj.host;
      return this.blinksByHost[host] ?? null;
    } catch (e) {
      console.error(
        `[@dialectlabs/blinks] Failed to lookup action for URL: ${url}`,
        e
      );
      return null;
    }
  }
  lookupWebsite(url) {
    try {
      const urlObj = new URL(url);
      const host = urlObj.host;
      return this.websitesByHost[host] ?? null;
    } catch (e) {
      console.error(
        `[@dialectlabs/blinks] Failed to lookup website for URL: ${url}`,
        e
      );
      return null;
    }
  }
  lookupInterstitial(url) {
    try {
      const urlObj = new URL(url);
      const host = urlObj.host;
      return this.interstitialsByHost[host] ?? null;
    } catch (e) {
      console.error(
        `[@dialectlabs/blinks] Failed to lookup interstitial for URL: ${url}`,
        e
      );
      return null;
    }
  }
};
var mergeBlinkStates = (...states) => {
  if (states.includes("malicious")) {
    return "malicious";
  }
  if (states.includes("unknown")) {
    return "unknown";
  }
  return "trusted";
};
var getExtendedBlinkState = (blinkOrUrl) => {
  return BlinksRegistry.getInstance().lookup(
    typeof blinkOrUrl === "string" ? blinkOrUrl : blinkOrUrl.url,
    "blink"
  )?.state ?? "unknown";
};
var getExtendedWebsiteState = (url) => {
  return BlinksRegistry.getInstance().lookup(url, "website")?.state ?? "unknown";
};
var getExtendedInterstitialState = (url) => {
  return BlinksRegistry.getInstance().lookup(url, "interstitial")?.state ?? "unknown";
};
async function fetchBlinksRegistryConfig() {
  try {
    const response = await fetch("https://actions-registry.dial.to/all");
    if (!response.ok) {
      console.error(
        "[@dialectlabs/blinks] Failed to fetch actions registry config",
        await response.json()
      );
      return { actions: [], interstitials: [], websites: [] };
    }
    return await response.json();
  } catch (e) {
    console.error(
      "[@dialectlabs/blinks] Failed to fetch actions registry config",
      e
    );
    return { actions: [], interstitials: [], websites: [] };
  }
}

export {
  BlockchainIds,
  BLINK_CLIENT_KEY_HEADER,
  clientKey,
  setClientKey,
  SOLANA_ACTION_PREFIX,
  EMPTY_OBJECT,
  isInterstitial,
  setProxyUrl,
  proxify,
  proxifyImage,
  proxifyMetadata,
  isProxified,
  shouldIgnoreProxy,
  shouldPreserveOriginal,
  checkSecurity,
  getBlinkSupportabilityMetadata,
  BlinksURLMapper,
  unfurlUrlToBlinkApiUrl,
  MAX_SUPPORTED_ACTION_VERSION,
  DEFAULT_SUPPORTED_BLOCKCHAIN_IDS,
  BASELINE_ACTION_VERSION,
  BASELINE_BLINK_BLOCKCHAIN_IDS,
  defaultBlinkSupportStrategy,
  isVersionSupported,
  isBlockchainSupported,
  BlinkInstance6 as BlinkInstance,
  AbstractActionComponent,
  ButtonActionComponent,
  SingleValueActionComponent,
  FormActionComponent,
  MultiValueActionComponent,
  isPatternAllowed,
  isParameterSelectable,
  SignMessageVerificationErrorType,
  createSignMessageText,
  parseSignMessageText,
  verifySignMessageData,
  BlinksRegistry,
  mergeBlinkStates,
  getExtendedBlinkState,
  getExtendedWebsiteState,
  getExtendedInterstitialState,
  fetchBlinksRegistryConfig
};
